import Service from '@root/common/base/Service';
import { ServiceInterface } from '@root/common/types/service';

import { Config } from '@root/modules/channel/types/channel';
import { GetArticleByID, Data as ArticleData } from '@root/modules/article/types/article';
import getArticleByID from '@root/modules/article/graphql/getArticleByID.graphql';
import getArticleBodyByID from '@root/modules/article/graphql/getArticleBodyByID.graphql';

import getAccessVariables from '@root/modules/article/utils/getAccessVariables';

import { ArticleAdapter } from '../utils/ArticleAdapter';

import redisClient from '@root/libs/redis/redisClient';

type ServiceVariables = {
  id: number | string;
  language?: string;
  preview?: string | null;
  zlickToken?: string | null;
  customerToken?: string | null;
  application?: Config['settings']['application'];
  settings?: {
    articleBaseDataExists: boolean;
    requestBody: boolean;
    authorizationHeader?: boolean;
  };
  getViews?: boolean;
};

type FetchVariables = GetArticleByID['variables'];

type Response = GetArticleByID['data'];

export default class ArticleService extends Service implements ServiceInterface {
  private getQueryWithOptions(serviceVariables: ServiceVariables) {
    const { id, preview, customerToken, zlickToken, application, settings, language, getViews } = serviceVariables;
    const variables: FetchVariables = { id: Number(id), authorLanguage: language, getViews };

    const { accessToken: articleToken, accessType } = getAccessVariables({ application, zlickToken: zlickToken || null, customerToken: customerToken || null });
    const query = settings?.articleBaseDataExists ? getArticleBodyByID : getArticleByID;

    const queryWithOptions = {
      query,
      options: {
        variables,
        context: { headers: {} as Record<string, unknown> },
      },
      articleToken,
    };

    if (preview) {
      queryWithOptions.options.context.headers.preview = preview;
      return queryWithOptions;
    }

    if (!articleToken) {
      return queryWithOptions;
    }

    switch (accessType) {
      case 'Customer':
        // Provide authorization header only on article body request
        if (settings?.authorizationHeader) {
          queryWithOptions.options.context.headers.Authorization = `Bearer ${articleToken}`;
        }
        break;
      case 'Zlick':
        queryWithOptions.options.context.headers['zlick-token'] = articleToken;
        break;
      case 'ElisaRaamat':
        queryWithOptions.options.context.headers['x-signature'] = articleToken;
        break;
      default:
        break;
    }

    return queryWithOptions;
  }

  private async fetchArticle(variables: ServiceVariables, channelConfig: Config) {
    const apiProvider = this.createProvider('GraphQL');
    const cacheKey = `fetchArticleGrouped:${JSON.stringify(variables)}`;
    let article: ArticleData | undefined;
    const { preview, application } = variables;

    apiProvider.selectAPI('content-api-v3');
    apiProvider.setLinkOptions({ useAutomaticPersistedQueries: true, useGETAutomaticPersistedQueries: true });

    if (isNaN(Number(variables.id))) {
      this.handleError();
    }

    // Set getViews for Video channel
    if (channelConfig.settings.id === 'video') {
      variables.getViews = true;
    }

    const { query, options: queryOptions, articleToken } = this.getQueryWithOptions(variables);

    if (redisClient && !preview && !application?.isSearchBot) {
      article = await redisClient.getFromRedis(cacheKey);
    }

    if (!article) {
      const options = Object.assign({ query }, queryOptions);
      const response = await apiProvider.request<Response>(options);

      if (!(response.article.data.length > 0)) {
        this.handleError();
      }

      const articleAdapter = new ArticleAdapter(response.article.data[0], channelConfig.page.article.component);
      article = articleAdapter.adapt();

      if (redisClient && !preview && !application?.isSearchBot) {
        await redisClient.setToRedis(cacheKey, article, 30);
      }
    }

    return {
      article,
      access: article.content.paywall.access,
      articleToken,
    };
  }

  public handleError() {
    throw {
      message: 'article.error.not_found',
      statusCode: 404,
    };
  }

  public async fetch(variables: { variables: ServiceVariables; channelConfig: Config }) {
    const isHealthy = this.getHealthCheckStatus('content-api-v3');

    if (!isHealthy) {
      this.handleError();
    }

    const response = await this.fetchArticle(variables.variables, variables.channelConfig);

    return response;
  }
}

export function fetchArticle(variables: ServiceVariables, { req, channelConfig }: { req: PortalRootIncomingMessage; channelConfig: Config }) {
  const articleService = new ArticleService({ req });
  return articleService.fetch({ variables, channelConfig });
}
